import { useState } from "react";
import { Link } from "react-router-dom";
import { sciezkaLinku, type RecenzjaRozmowaPodsumowanie, type StanRozmowyRecenzenta } from "@shared/recenzjeTypy.js";
import { odmien } from "../../lib/polski.js";
import { ETYKIETY_STANU, opisPostepu, tekstBledu } from "../../lib/recenzja.js";
import { recenzjaApi } from "../../lib/recenzjaApi.js";
import { zapiszBlobJakoPlik } from "../../lib/zapiszPlik.js";
import { IconPobierz } from "../icons/index.js";
import { Badge, type WariantBadge } from "../ui/Badge.js";
import { Button } from "../ui/Button.js";
import { useToast } from "../ui/Toast.js";
import { PasekPostepu } from "./PasekPostepu.js";

/**
 * Stan przegladu to prawdziwy status, nie ocena rozmowy. "W toku" jest informacja, nie ostrzezeniem: w linku recenzenta
 * bursztyn znaczy "najlepsze" (zloto oceny w przegladzie rozmowy), wiec stan w toku nie moze go uzywac.
 */
const WARIANTY_STANU: Readonly<Record<StanRozmowyRecenzenta, WariantBadge>> = {
  NIE_ZACZETA: "neutralny",
  W_TOKU: "info",
  PRZEJRZANA: "success",
};

/**
 * Wiersz listy rozmow pod linkiem: tytul, stan (slowem, nie kolorem), liczby, pasek "N z M wskazan" i akcje.
 * "Otworz" jest primary tylko wtedy, gdy rozmowa czeka na przeglad (`doZrobienia`); reszta to secondary (B-2).
 */
export function WierszRozmowy({
  token,
  rozmowa,
  doZrobienia,
}: {
  token: string;
  rozmowa: RecenzjaRozmowaPodsumowanie;
  /** Pierwsza rozmowa, ktorej przeglad nie jest jeszcze zakonczony: dostaje wyrozniony przycisk. */
  doZrobienia: boolean;
}): React.JSX.Element {
  const toast = useToast();
  const [pobiera, setPobiera] = useState(false);

  async function pobierzWord(): Promise<void> {
    if (pobiera) return;
    setPobiera(true);
    try {
      const { blob, nazwaPliku } = await recenzjaApi.pobierzWord(token, rozmowa.id);
      zapiszBlobJakoPlik(blob, nazwaPliku);
      toast.sukces("Plik Word pobrany.");
    } catch (err) {
      toast.blad(tekstBledu(err));
    } finally {
      setPobiera(false);
    }
  }

  return (
    <li className="rowlist__row rec-wiersz">
      <div className="rowlist__body rec-wiersz__tresc">
        <p className="rowlist__nazwa rec-wiersz__nazwa">
          <span className="rec-wiersz__tytul">{rozmowa.tytul}</span>
          <Badge wariant={WARIANTY_STANU[rozmowa.stan]} kropka>
            {ETYKIETY_STANU[rozmowa.stan]}
          </Badge>
        </p>
        <p className="rowlist__meta">
          {`${rozmowa.liczbaWypowiedzi} ${odmien(rozmowa.liczbaWypowiedzi, "wypowiedź", "wypowiedzi", "wypowiedzi")} · ${rozmowa.liczbaMowcow} ${odmien(rozmowa.liczbaMowcow, "mówca", "mówców", "mówców")}`}
        </p>
        <div className="rec-wiersz__postep">
          <PasekPostepu postep={rozmowa.postep} etykieta={`Postęp przeglądu: ${rozmowa.tytul}`} />
          <span>{opisPostepu(rozmowa.postep)}</span>
        </div>
      </div>
      <div className="rowlist__actions">
        <Button wariant="secondary" zajety={pobiera} onClick={() => void pobierzWord()} aria-label={`Pobierz Word: ${rozmowa.tytul}`}>
          {!pobiera && <IconPobierz />}
          {pobiera ? "Przygotowuję..." : "Word"}
        </Button>
        <Link className={`btn ${doZrobienia ? "btn--primary" : "btn--secondary"}`} to={`${sciezkaLinku(token)}/rozmowa/${encodeURIComponent(rozmowa.id)}`} aria-label={`Otwórz: ${rozmowa.tytul}`}>
          Otwórz
        </Link>
      </div>
    </li>
  );
}
